import type { ReactNode } from "react";
import { Effect, Layer, Match } from "effect";
import { INTERNAL_HTTP_MESSAGE } from "@workerstation/shared/errors/http";
import { mountRoot } from "./state/mount-root.tsx";
import type { FrontendRoot } from "./state/root-owner.ts";

export type FrontendHostResult<A> = { readonly ok: true; readonly value: A } | { readonly ok: false; readonly message: string };
export interface FrontendApplication<R> {
  readonly root: FrontendRoot<R>;
  readonly close: () => Promise<FrontendHostResult<void>>;
}
const project = <A, E>(exit: import("effect").Exit.Exit<A, E>): FrontendHostResult<A> => Match.value(exit).pipe(
  Match.tag("Failure", () => ({ ok: false as const, message: INTERNAL_HTTP_MESSAGE })),
  Match.tag("Success", result => ({ ok: true as const, value: result.value })),
  Match.exhaustive
);

/** This is the sole application runner edge; domains expose Effects only. */
export function mountApplication<R, E>(container: Element, graph: Layer.Layer<R, E>, content: (root: FrontendRoot<R>) => ReactNode, shutdown: Effect.Effect<void> = Effect.void, page: Pick<Window, "addEventListener" | "removeEventListener"> = window): Promise<FrontendHostResult<FrontendApplication<R>>> {
  let application: FrontendApplication<R> | undefined;
  const startup = new AbortController();
  const onPageHide = (event: Event) => {
    if ((event as PageTransitionEvent).persisted) return;
    if (application) void application.close();
    else startup.abort();
  };
  const cleanup = Effect.sync(() => page.removeEventListener("pagehide", onPageHide)).pipe(Effect.ensuring(shutdown));
  page.addEventListener("pagehide", onPageHide);
  return Effect.runPromiseExit(mountRoot(container, graph, content, cleanup), { signal: startup.signal }).then(exit => {
    const result = project(exit);
    if (!result.ok) {
      page.removeEventListener("pagehide", onPageHide);
      return result;
    }
    application = { root: result.value, close: () => Effect.runPromiseExit(result.value.close).then(project) };
    return { ok: true, value: application };
  });
}
